import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { ShowProductResponse } from '../../../../../setup/apiTypes/warehouse';
import Button from '../../../../../components/atoms/Button';
import { ButtonKind } from '../../../../../components/atoms/Button/enums';
import Badge from '../../../../../components/atoms/Badge';
import { BadgeTone } from '../../../../../components/atoms/Badge/enums';
import ScCard from '../../../../../components/atoms/ScCard';
import ScStat from '../../../../../components/molecules/ScStat';
import ScSectionHeader from '../../../../../components/molecules/ScSectionHeader';
import Avatar from '../../../../../components/atoms/Avatar';
import {
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconClose,
  IconUpload,
  IconInfo,
  IconWarehouse,
  IconRefresh,
  IconArrowForward,
  IconBox,
  WarningIcon,
} from '../../../../../assets/svg/icons';

type Props = { product: ShowProductResponse['product'] | null };


const TONE_COLOR: Record<string, string> = {
  success: 'var(--brand-green)',
  danger:  'var(--danger)',
  warning: 'var(--warning)',
  info:    'var(--info)',
  neutral: 'var(--muted-2)',
};


/* ─── Field primitives for Edit drawer ─── */
const TextInputField: React.FC<{
  defaultValue?: string | number;
  placeholder?: string;
  suffix?: string;
  prefix?: string;
  ar?: boolean;
  mono?: boolean;
  readOnly?: boolean;
}> = ({ defaultValue, placeholder, suffix, prefix, ar, mono, readOnly }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '0 12px', height: 38,
    background: readOnly ? 'var(--paper)' : 'var(--surface)',
    border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
  }}>
    {prefix && <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{prefix}</span>}
    <input
      defaultValue={defaultValue}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{
        flex: 1, border: 0, outline: 'none', background: 'transparent',
        fontSize: 'var(--t-md)', color: readOnly ? 'var(--muted)' : 'var(--ink)',
        fontFamily: ar ? 'inherit' : mono ? 'var(--font-mono, monospace)' : 'inherit',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        textAlign: ar ? 'right' : 'left',
        direction: ar ? 'rtl' : 'ltr',
      }}
    />
    {suffix && <span style={{ color: 'var(--muted)', fontSize: 11 }}>{suffix}</span>}
  </div>
);

const SelectField: React.FC<{ defaultValue?: string; options: string[] }> = ({ defaultValue, options }) => (
  <select defaultValue={defaultValue} style={{
    width: '100%', height: 38, padding: '0 12px',
    border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
    background: 'var(--surface)', fontSize: 'var(--t-md)', fontFamily: 'inherit',
    color: 'var(--ink)', outline: 'none',
  }}>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const FieldLabel: React.FC<{ children: React.ReactNode; hint?: string }> = ({ children, hint }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
    <label style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)' }}>{children}</label>
    {hint && <span style={{ fontSize: 11, color: 'var(--muted)' }}>{hint}</span>}
  </div>
);

const EditField: React.FC<{ label: string; hint?: string; full?: boolean; children: React.ReactNode }> = ({ label, hint, full, children }) => (
  <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
    <FieldLabel hint={hint}>{label}</FieldLabel>
    {children}
  </div>
);

/* ─── KV row used in pricing / stock / details cards ─── */
const KV: React.FC<{ k: string; v: React.ReactNode; last?: boolean }> = ({ k, v, last }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 0', gap: 12,
    borderBottom: last ? 'none' : '1px solid var(--line)',
  }}>
    <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', fontWeight: 500 }}>{k}</span>
    <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, textAlign: 'right', minWidth: 0 }}>{v}</span>
  </div>
);

const Row2sm: React.FC<{ label: string; value: React.ReactNode; last?: boolean }> = ({ label, value, last }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', padding: '7px 0',
    borderBottom: last ? 'none' : '1px solid var(--line)', fontSize: 'var(--t-sm)',
  }}>
    <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);

/* ─── Edit drawer tab bodies ─── */
const EditBasics: React.FC<{ p: ShowProductResponse['product'] }> = ({ p }) => {
  const intl = useIntl();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-5) var(--s-4)' }}>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.arabicName', defaultMessage: 'Arabic name' })} full><TextInputField defaultValue={p.name} ar /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.englishName', defaultMessage: 'English name' })} full><TextInputField defaultValue="" placeholder="English product name…" /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.category', defaultMessage: 'Category' })}>
        <SelectField defaultValue={p.categoryName} options={[p.categoryName, 'منوعات', 'إلكترونيات', 'مستحضرات تجميل']} />
      </EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.businessType', defaultMessage: 'Business type' })}>
        <SelectField defaultValue="Home & Personal care" options={['Home & Personal care', 'DIY & Tools', 'Home & Kitchen', 'Electronics']} />
      </EditField>
      <EditField label="SKU" hint={intl.formatMessage({ id: 'warehouse.edit.skuHint', defaultMessage: 'Cannot be changed' })}><TextInputField defaultValue={p.sku} mono readOnly /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.barcode', defaultMessage: 'Barcode (EAN/UPC)' })}><TextInputField defaultValue={p.specification?.ean ?? ''} mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.weight', defaultMessage: 'Weight' })}><TextInputField defaultValue="" suffix="g" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.dimensions', defaultMessage: 'Dimensions' })} hint={intl.formatMessage({ id: 'warehouse.edit.dimensionsHint', defaultMessage: 'L × W × H, cm' })}><TextInputField defaultValue="" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.shortDescription', defaultMessage: 'Short description' })} full>
        <textarea
          defaultValue={p.description ?? ''}
          style={{
            width: '100%', minHeight: 80, resize: 'vertical', padding: '10px 12px',
            border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
            fontFamily: 'inherit', fontSize: 13, outline: 'none',
            background: 'var(--surface)', color: 'var(--ink)', boxSizing: 'border-box',
          }}
        />
      </EditField>
    </div>
  );
};

const EditPricing: React.FC<{ p: ShowProductResponse['product'] }> = ({ p }) => {
  const intl = useIntl();
  const wholesale = p.costPrice ?? 0;
  const retail = p.sellingPrice ?? 0;
  const margin = wholesale > 0 ? Math.round((retail - wholesale) / wholesale * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-5)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-5) var(--s-4)' }}>
        <EditField label={intl.formatMessage({ id: 'warehouse.edit.wholesalePrice', defaultMessage: 'Wholesale price' })}><TextInputField defaultValue={wholesale || ''} suffix="IQD" mono /></EditField>
        <EditField label={intl.formatMessage({ id: 'warehouse.edit.retailPrice', defaultMessage: 'Retail price' })}><TextInputField defaultValue={retail || ''} suffix="IQD" mono /></EditField>
        <EditField label={intl.formatMessage({ id: 'warehouse.edit.cost', defaultMessage: 'Cost (your cost)' })}><TextInputField defaultValue="" suffix="IQD" mono /></EditField>
        <EditField label={intl.formatMessage({ id: 'warehouse.edit.currency', defaultMessage: 'Currency' })}>
          <SelectField defaultValue="IQD · Iraqi Dinar" options={['IQD · Iraqi Dinar', 'USD · US Dollar']} />
        </EditField>
      </div>
      {margin !== 0 && (
        <div style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--success-soft)', color: 'var(--success-ink)', display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>+{margin}%</span>
          <span style={{ fontSize: 13 }}>{intl.formatMessage({ id: 'warehouse.marginHint', defaultMessage: 'margin · {margin} IQD per unit' }, { margin: (retail - wholesale).toLocaleString() })}</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'warehouse.edit.bulkSale', defaultMessage: 'Available for bulk sale' })}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{intl.formatMessage({ id: 'warehouse.edit.bulkSaleHint', defaultMessage: 'Visible on the wholesale marketplace' })}</div>
        </div>
        <button style={{ width: 44, height: 24, borderRadius: 12, padding: 2, background: 'var(--brand-green)', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'block', marginLeft: 'auto', transition: 'transform var(--dur-base) var(--ease-out)' }} />
        </button>
      </div>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.minBulkQty', defaultMessage: 'Min bulk quantity' })}><TextInputField defaultValue="20" suffix="units" mono /></EditField>
    </div>
  );
};

const EditStock: React.FC<{ p: ShowProductResponse['product'] }> = ({ p }) => {
  const intl = useIntl();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-5) var(--s-4)' }}>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.onHand', defaultMessage: 'On hand (live)' })} hint={intl.formatMessage({ id: 'warehouse.edit.onHandHint', defaultMessage: 'Read-only — sync to update' })}><TextInputField defaultValue="—" suffix="units" mono readOnly /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.damaged', defaultMessage: 'Damaged' })}><TextInputField defaultValue="0" suffix="units" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.lowStockThreshold', defaultMessage: 'Low-stock threshold' })} hint={intl.formatMessage({ id: 'warehouse.edit.lowStockThresholdHint', defaultMessage: 'Alert below this level' })}><TextInputField defaultValue="5" suffix="units" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.reorderQty', defaultMessage: 'Reorder quantity' })} hint={intl.formatMessage({ id: 'warehouse.edit.reorderQtyHint', defaultMessage: 'Default for purchase orders' })}><TextInputField defaultValue="120" suffix="units" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.location', defaultMessage: 'Warehouse location' })}><TextInputField defaultValue="A-12-3" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.supplier', defaultMessage: 'Supplier' })}>
        <SelectField defaultValue="TAG Wholesale · Baghdad" options={['TAG Wholesale · Baghdad', 'TAG Wholesale · Mosul', 'MCA Distribution', 'Direct import']} />
      </EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.leadTime', defaultMessage: 'Lead time' })}><TextInputField defaultValue="5" suffix="days" mono /></EditField>
      <EditField label={intl.formatMessage({ id: 'warehouse.edit.entitiesLabel', defaultMessage: 'Available in entities' })} hint="2 selected" full>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {['TAG', 'TAG Refund', 'north of iraq', 'TAG Exchange', 'TAG Compensation'].map((e, i) => (
            <button key={e} style={{
              padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 500, cursor: 'pointer',
              border: 0,
              background: i < 2 ? 'var(--brand-ink)' : 'var(--paper-2)',
              color: i < 2 ? '#fff' : 'var(--ink-2)',
            }}>{i < 2 && '✓ '}{e}</button>
          ))}
        </div>
      </EditField>
    </div>
  );
};

const EditPhotos: React.FC<{ p: ShowProductResponse['product'] }> = ({ p }) => {
  const intl = useIntl();
  const allImages = [p.coverImage, ...(p.images ?? [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 4);
  const slots = [...allImages, ...Array(6 - allImages.length).fill(null)];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
      <div style={{ padding: '16px 18px', borderRadius: 'var(--r-md)', background: 'var(--info-soft)', color: 'var(--info-ink)', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center' }}>
        <IconInfo style={{ width: 16, height: 16, flexShrink: 0 }} />
        <span>{intl.formatMessage({ id: 'warehouse.edit.photosHint', defaultMessage: 'Upload 1–6 photos. First photo is the cover. PNG or JPG up to 4 MB each.' })}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {slots.map((src, i) =>
          src ? (
            <div key={i} style={{ aspectRatio: '1/1', borderRadius: 'var(--r-md)', background: '#FCE4EC', border: '1px solid var(--line)', position: 'relative', overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
              <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
              {i === 0 && (
                <span style={{ position: 'absolute', top: 8, left: 8, padding: '2px 8px', borderRadius: 999, background: 'var(--brand-green)', color: '#fff', fontSize: 11, fontWeight: 600 }}>{intl.formatMessage({ id: 'warehouse.edit.photoCover', defaultMessage: 'Cover' })}</span>
              )}
              <button style={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,.95)', border: 0, cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 12 }}>✕</button>
            </div>
          ) : (
            <div key={i} style={{ aspectRatio: '1/1', borderRadius: 'var(--r-md)', border: '1px dashed var(--line-2)', background: 'var(--paper)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', gap: 4, cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>+</span>
              <span style={{ fontSize: 11 }}>{intl.formatMessage({ id: 'warehouse.edit.photoUpload', defaultMessage: 'Upload' })}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const EditMeta: React.FC<{ p: ShowProductResponse['product'] }> = ({ p }) => {
  const intl = useIntl();
  const toggleItems = [
    { label: intl.formatMessage({ id: 'warehouse.edit.archived', defaultMessage: 'Archived' }), hint: intl.formatMessage({ id: 'warehouse.edit.archivedHint', defaultMessage: 'Hides from the storefront and marketplace' }) },
    { label: intl.formatMessage({ id: 'warehouse.edit.pauseSales', defaultMessage: 'Pause sales' }), hint: intl.formatMessage({ id: 'warehouse.edit.pauseSalesHint', defaultMessage: 'Block new orders for this SKU' }) },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-5)' }}>
      <div>
        <FieldLabel hint={intl.formatMessage({ id: 'warehouse.edit.tagsHint', defaultMessage: 'Press enter to add' })}>{intl.formatMessage({ id: 'warehouse.edit.tags', defaultMessage: 'Tags' })}</FieldLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', background: 'var(--surface)', alignItems: 'center' }}>
          {['hair-removal', 'grooming', 'small-appliance'].map(tag => (
            <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 4px 4px 10px', borderRadius: 999, background: 'var(--paper-2)', color: 'var(--ink-2)', fontSize: 11, fontWeight: 500 }}>
              {tag}
              <button style={{ width: 18, height: 18, borderRadius: '50%', border: 0, background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 11, color: 'var(--muted)' }}>✕</button>
            </span>
          ))}
          <input placeholder="Add tag…" style={{ flex: 1, minWidth: 120, border: 0, outline: 'none', background: 'transparent', fontSize: 13, fontFamily: 'inherit' }} />
        </div>
      </div>
      {toggleItems.map(({ label, hint }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{label}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{hint}</div>
          </div>
          <button style={{ width: 44, height: 24, borderRadius: 12, padding: 2, background: 'var(--muted-3)', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'block' }} />
          </button>
        </div>
      ))}
      <div style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--danger-soft)', border: '1px solid var(--danger-soft)', display: 'flex', gap: 14, alignItems: 'center' }}>
        <div style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'rgba(212,121,116,.18)', color: 'var(--danger)', display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 18 }}>🗑</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--danger-ink)' }}>{intl.formatMessage({ id: 'warehouse.edit.deleteProduct', defaultMessage: 'Delete product' })}</div>
          <div style={{ fontSize: 11, color: 'var(--danger-ink)', opacity: 0.8, marginTop: 2 }}>{intl.formatMessage({ id: 'warehouse.edit.deleteProductHint', defaultMessage: 'Removes the SKU from all entities. Orders already placed are kept.' })}</div>
        </div>
        <button style={{ padding: '6px 14px', borderRadius: 'var(--r-md)', background: 'var(--danger)', color: '#fff', border: 0, cursor: 'pointer', fontSize: 'var(--t-sm)', fontWeight: 600, whiteSpace: 'nowrap' }}>{intl.formatMessage({ id: 'warehouse.edit.delete', defaultMessage: 'Delete' })}</button>
      </div>
    </div>
  );
};

/* ─── Edit drawer ─── */
const EditDrawer: React.FC<{ product: ShowProductResponse['product']; onClose: () => void }> = ({ product, onClose }) => {
  const intl = useIntl();
  const [tab, setTab] = useState('basics');
  const tabs = [
    { id: 'basics',   label: intl.formatMessage({ id: 'warehouse.edit.tabBasics', defaultMessage: 'Basics' }) },
    { id: 'pricing',  label: intl.formatMessage({ id: 'warehouse.edit.tabPricing', defaultMessage: 'Pricing' }) },
    { id: 'stock',    label: intl.formatMessage({ id: 'warehouse.edit.tabStock', defaultMessage: 'Stock & alerts' }) },
    { id: 'photos',   label: intl.formatMessage({ id: 'warehouse.edit.tabPhotos', defaultMessage: 'Photos' }) },
    { id: 'meta',     label: intl.formatMessage({ id: 'warehouse.edit.tabMeta', defaultMessage: 'Meta' }) },
  ];
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(9,15,56,.45)', backdropFilter: 'blur(4px)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(640px, 92vw)', height: '100vh',
          background: 'var(--surface)', display: 'flex', flexDirection: 'column',
          boxShadow: 'var(--sh-4)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 'var(--r-md)', background: 'var(--paper-2)', color: 'var(--muted)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <IconEdit style={{ width: 18, height: 18 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--t-lg)', fontWeight: 700, letterSpacing: '-0.01em' }}>{intl.formatMessage({ id: 'warehouse.editProduct', defaultMessage: 'Edit product' })}</div>
            <div className="ar" style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {product.name}
            </div>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: 'var(--r-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--muted)', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0, padding: 0 }}
          >
            <IconClose style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {/* Sub-tabs */}
        <div style={{ padding: '0 24px', borderBottom: '1px solid var(--line)', display: 'flex', gap: 24, flexShrink: 0 }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '14px 0', border: 0, background: 'transparent', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'var(--t-sm)',
                fontWeight: tab === t.id ? 600 : 500,
                color: tab === t.id ? 'var(--ink)' : 'var(--muted)',
                borderBottom: `2px solid ${tab === t.id ? 'var(--brand-green)' : 'transparent'}`,
                marginBottom: -1, whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--s-6) 24px' }}>
          {tab === 'basics'  && <EditBasics  p={product} />}
          {tab === 'pricing' && <EditPricing p={product} />}
          {tab === 'stock'   && <EditStock   p={product} />}
          {tab === 'photos'  && <EditPhotos  p={product} />}
          {tab === 'meta'    && <EditMeta    p={product} />}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--line)', background: 'var(--paper)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{intl.formatMessage({ id: 'warehouse.edit.autoSaveHint', defaultMessage: 'Changes save instantly to all entities.' })}</span>
          <div style={{ flex: 1 }} />
          <Button kind={ButtonKind.Ghost} onClick={onClose}>{intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}</Button>
          <Button kind={ButtonKind.Primary} onClick={onClose}>{intl.formatMessage({ id: 'common.saveChanges', defaultMessage: 'Save changes' })}</Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main ProductDetails component ─── */
const ProductDetails: React.FC<Props> = ({ product }) => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const [editOpen, setEditOpen] = useState(false);

  const TIMELINE_MOCK = [
    { label: intl.formatMessage({ id: 'warehouse.activityLog.orderPlaced',  defaultMessage: 'Order placed' }),   sub: 'TAG · 1 unit (Picked Up)',         tone: 'success', active: true,  date: '24/05/2026 · 2:11 PM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.stockReturned',defaultMessage: 'Stock returned' }), sub: '1 unit returned from failed order', tone: 'info',    active: false, date: '24/05/2026 · 9:02 AM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.restocked',    defaultMessage: 'Restocked' }),      sub: '120 units received from supplier',  tone: 'success', active: false, date: '23/05/2026 · 4:48 PM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.lowStockAlert',defaultMessage: 'Low stock alert' }),sub: 'Threshold of 5 units crossed',     tone: 'warning', active: false, date: '21/05/2026 · 8:14 AM'  },
    { label: intl.formatMessage({ id: 'warehouse.activityLog.skuCreated',   defaultMessage: 'SKU created' }),    sub: 'By Ahmed Saber',                    tone: 'neutral', active: false, date: '24/01/2024 · 10:00 AM' },
  ];

  useEffect(() => {
    if (new URLSearchParams(location.search).get('edit') === '1') {
      setEditOpen(true);
    }
  }, [location.search]);

  const openEdit = () => setEditOpen(true);
  const closeEdit = () => {
    setEditOpen(false);
    if (new URLSearchParams(location.search).get('edit')) {
      history.replace(location.pathname);
    }
  };

  if (!product) {
    return <p style={{ color: 'var(--muted)', padding: 'var(--s-5)' }}>{intl.formatMessage({ id: 'warehouse.couldntLoadProduct', defaultMessage: "Couldn't load product data." })}</p>;
  }

  const {
    name, sku, categoryName, coverImage, images,
    sellingPrice, costPrice, createdOn, description,
    specification, warehouseProducts,
  } = product;

  const onHand = Array.isArray(warehouseProducts)
    ? (warehouseProducts as any[]).reduce((s: number, w: any) => s + (w?.availableQuantity ?? 0), 0)
    : 0;

  const status: 'ok' | 'low' | 'out' = onHand === 0 ? 'out' : onHand <= 5 ? 'low' : 'ok';
  const statusTone = ({ ok: BadgeTone.Success, low: BadgeTone.Warning, out: BadgeTone.Danger } as const)[status];
  const statusLabel = {
    ok:  intl.formatMessage({ id: 'warehouse.inStock', defaultMessage: 'In stock' }),
    low: intl.formatMessage({ id: 'dashboard.lowStock', defaultMessage: 'Low stock' }),
    out: intl.formatMessage({ id: 'warehouse.outOfStock', defaultMessage: 'Out of stock' }),
  }[status];
  const statusBannerBg = { ok: 'var(--success-soft)', low: 'var(--warning-soft)', out: 'var(--danger-soft)' }[status];
  const statusBannerInk = { ok: 'var(--success-ink)', low: 'var(--warning-ink)', out: 'var(--danger-ink)' }[status];
  const statusBannerTitle = {
    ok:  intl.formatMessage({ id: 'warehouse.stockBanner.okTitle' }, { count: onHand }),
    low: intl.formatMessage({ id: 'warehouse.stockBanner.lowTitle' }, { count: onHand }),
    out: intl.formatMessage({ id: 'warehouse.outOfStock', defaultMessage: 'Out of stock' }),
  }[status];
  const statusBannerBody = {
    ok:  intl.formatMessage({ id: 'warehouse.stockBanner.okBody' }),
    low: intl.formatMessage({ id: 'warehouse.stockBanner.lowBody' }),
    out: intl.formatMessage({ id: 'warehouse.stockBanner.outBody' }),
  }[status];

  const wholesale = costPrice ?? 0;
  const retail = sellingPrice ?? 0;
  const margin = wholesale > 0 && retail > 0 ? Math.round((retail - wholesale) / wholesale * 100) : null;

  const formattedCreated = (() => {
    if (!createdOn) return '—';
    const d = new Date(createdOn);
    if (isNaN(d.getTime())) return createdOn;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();

  const allImages = [coverImage, ...(images ?? [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const photoSlots = [...allImages.slice(0, 4), ...Array(Math.max(0, 4 - allImages.length)).fill(null)];


  return (
    <>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--s-4)' }}>
        <button
          onClick={() => history.push('/warehouse')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: 'var(--r-md)',
            background: 'transparent', border: '1px solid var(--line)',
            color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 'var(--t-sm)', fontWeight: 500,
          }}
        >
          <IconChevronLeft style={{ width: 14, height: 14 }} />
          <span>{intl.formatMessage({ id: 'mainNavigation.warehouse', defaultMessage: 'Warehouse' })}</span>
        </button>
        <span style={{ color: 'var(--muted-2)', display: 'flex' }}>
          <IconChevronRight style={{ width: 14, height: 14 }} />
        </span>
        <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink)' }}>{intl.formatMessage({ id: 'warehouse.product', defaultMessage: 'Product' })}</span>
      </div>

      {/* Header block */}
      <div style={{ marginBottom: 'var(--s-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <Badge tone={statusTone} dot>{statusLabel}</Badge>
          {categoryName && <Badge tone={BadgeTone.Neutral}><span className="ar">{categoryName}</span></Badge>}
          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'warehouse.updatedJustNow', defaultMessage: 'updated just now' })}</span>
        </div>
        <h1 className="ar" style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.15, margin: 0 }}>
          {name}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', marginTop: 8, flexWrap: 'wrap', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontVariantNumeric: 'tabular-nums' }}>{sku}</span>
          {specification?.ean && (
            <>
              <span style={{ color: 'var(--muted-2)' }}>·</span>
              <span>{intl.formatMessage({ id: 'warehouse.barcode', defaultMessage: 'Barcode' })} <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{specification.ean}</span></span>
            </>
          )}
          <span style={{ color: 'var(--muted-2)' }}>·</span>
          <span>{intl.formatMessage({ id: 'warehouse.created', defaultMessage: 'Created' })} {formattedCreated}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 'var(--s-4)', flexWrap: 'wrap' }}>
          <Button kind={ButtonKind.Outline} icon={IconEdit} onClick={openEdit}>{intl.formatMessage({ id: 'warehouse.editProduct', defaultMessage: 'Edit product' })}</Button>
          <Button kind={ButtonKind.Outline} icon={IconUpload}>{intl.formatMessage({ id: 'warehouse.restock', defaultMessage: 'Restock' })}</Button>
          <Button kind={ButtonKind.Primary} icon={IconArrowForward}>{intl.formatMessage({ id: 'warehouse.productCardButtonLabel', defaultMessage: 'Create order' })}</Button>
        </div>
      </div>

      {/* Status banner */}
      <div style={{
        padding: 'var(--s-5)', marginBottom: 'var(--s-5)', borderRadius: 'var(--r-lg)',
        background: statusBannerBg, border: `1px solid ${statusBannerBg}`,
        display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 16, alignItems: 'center',
        boxShadow: 'var(--sh-1)',
      }}>
        <div style={{ width: 48, height: 48, borderRadius: 'var(--r-md)', background: statusBannerBg, color: statusBannerInk, display: 'grid', placeItems: 'center', fontSize: 22 }}>
          {status === 'ok' ? '✓' : '⚠'}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)', color: statusBannerInk }}>{statusBannerTitle}</div>
          <div style={{ fontSize: 'var(--t-sm)', color: statusBannerInk, opacity: 0.85, marginTop: 4, maxWidth: 680 }}>{statusBannerBody}</div>
        </div>
        {status !== 'ok' && (
          <div style={{ flexShrink: 0 }}>
            <Button kind={ButtonKind.Outline}>{intl.formatMessage({ id: 'warehouse.restock', defaultMessage: 'Restock' })}</Button>
          </div>
        )}
      </div>

      {/* Main two-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 'var(--s-5)', alignItems: 'start' }}>

        {/* LEFT column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)', minWidth: 0 }}>

          {/* Gallery + Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 'var(--s-4)' }}>
            {/* Gallery */}
            <ScCard pad={false} style={{ padding: 'var(--s-4)' }}>
              <div style={{ aspectRatio: '5/4', borderRadius: 'var(--r-md)', background: '#FCE4EC', display: 'grid', placeItems: 'center', color: 'rgba(0,0,0,.18)', position: 'relative', overflow: 'hidden' }}>
                {allImages[0] ? (
                  <img src={allImages[0]} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <IconBox style={{ width: 64, height: 64, opacity: 0.4 }} />
                )}
                <button
                  title="Upload photo"
                  style={{ position: 'absolute', top: 10, right: 10, width: 34, height: 34, borderRadius: 'var(--r-sm)', border: 0, background: 'rgba(255,255,255,.92)', color: 'var(--ink-2)', cursor: 'pointer', display: 'grid', placeItems: 'center', backdropFilter: 'blur(4px)' }}
                >
                  <IconUpload style={{ width: 16, height: 16 }} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
                {photoSlots.map((src, i) => (
                  <button key={i} style={{ aspectRatio: '1/1', borderRadius: 'var(--r-sm)', background: ['#FCE4EC','#FBE9F0','#FFF1F4','#FDE7EB'][i] ?? 'var(--paper-2)', border: i === 0 ? '2px solid var(--brand-green)' : '1px solid var(--line)', padding: 0, cursor: 'pointer', display: 'grid', placeItems: 'center', color: 'rgba(0,0,0,.18)', overflow: 'hidden' }}>
                    {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <IconBox style={{ width: 18, height: 18 }} />}
                  </button>
                ))}
              </div>
            </ScCard>

            {/* Details card */}
            <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
              <ScSectionHeader title={intl.formatMessage({ id: 'warehouse.details', defaultMessage: 'Details' })} icon={<IconInfo style={{ width: 16, height: 16 }} />} />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <KV k={intl.formatMessage({ id: 'warehouse.englishName', defaultMessage: 'English name' })} v={name} />
                <KV k={intl.formatMessage({ id: 'warehouse.category', defaultMessage: 'Category' })} v={<span className="ar">{categoryName}</span>} />
                <KV k={intl.formatMessage({ id: 'warehouse.businessType', defaultMessage: 'Business type' })} v="—" />
                <KV k={intl.formatMessage({ id: 'warehouse.supplier', defaultMessage: 'Supplier' })} v="—" />
                {specification?.unitName && <KV k={intl.formatMessage({ id: 'warehouse.unit', defaultMessage: 'Unit' })} v={specification.unitName} />}
                <KV k={intl.formatMessage({ id: 'warehouse.description', defaultMessage: 'Description' })} v={<span style={{ maxWidth: 180, textAlign: 'right', fontSize: 12, color: 'var(--muted)' }}>{description?.slice(0, 60) || '—'}</span>} last />
              </div>
            </ScCard>
          </div>

          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--s-3)' }}>
            <ScStat label={intl.formatMessage({ id: 'warehouse.remaining', defaultMessage: 'Remaining' })} value={onHand} sub={intl.formatMessage({ id: 'warehouse.thresholdSub', defaultMessage: 'Threshold · 5 units' }, { threshold: 5 })} accent={status === 'ok' ? 'green' : status === 'low' ? 'warning' : 'danger'} />
            <ScStat label={intl.formatMessage({ id: 'warehouse.ordered', defaultMessage: 'Ordered' })} value="—" sub={intl.formatMessage({ id: 'warehouse.acrossAllEntities', defaultMessage: 'Across all entities' })} accent="ink" />
            <ScStat label={intl.formatMessage({ id: 'warehouse.damaged', defaultMessage: 'Damaged' })} value="—" sub={intl.formatMessage({ id: 'warehouse.pendingWriteoff', defaultMessage: 'Pending writeoff' })} accent="ink" />
            <ScStat label={intl.formatMessage({ id: 'warehouse.avgDailySold', defaultMessage: 'Avg daily sold' })} value="—" sub={intl.formatMessage({ id: 'warehouse.last14days', defaultMessage: 'Last 14 days' })} accent="green" />
          </div>

          {/* Sales analytics — coming soon */}
          <ScCard pad={false} style={{ padding: 'var(--s-6)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 160, textAlign: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--paper-2)', color: 'var(--muted)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-sm)', padding: '2px 8px' }}>
              {intl.formatMessage({ id: 'comingSoon.short', defaultMessage: 'Coming soon' })}
            </span>
            <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)', color: 'var(--ink)' }}>
              {intl.formatMessage({ id: 'warehouse.analyticsTitle', defaultMessage: 'Sales analytics' })}
            </div>
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', maxWidth: 320 }}>
              {intl.formatMessage({ id: 'warehouse.analyticsDesc', defaultMessage: 'Sales trend, by-entity breakdown, and recent orders will appear here once the analytics endpoint is available.' })}
            </div>
          </ScCard>
        </div>

        {/* RIGHT rail — sticky */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)', minWidth: 0, position: 'sticky', top: 'calc(var(--topbar-h, 64px) + 24px)' }}>

          {/* Pricing */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader
              title={intl.formatMessage({ id: 'warehouse.pricing', defaultMessage: 'Pricing' })}
              actions={<Button kind={ButtonKind.Ghost} icon={IconEdit} onClick={openEdit}>{intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}</Button>}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <KV k={intl.formatMessage({ id: 'warehouse.wholesale', defaultMessage: 'Wholesale' })} v={<><span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{wholesale.toLocaleString()}</span> <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>IQD</span></>} />
              <KV k={intl.formatMessage({ id: 'warehouse.retail', defaultMessage: 'Retail' })} v={<><span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{retail.toLocaleString()}</span> <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>IQD</span></>} />
              <KV k={intl.formatMessage({ id: 'warehouse.margin', defaultMessage: 'Margin' })} v={<span style={{ color: 'var(--success-ink)', fontWeight: 600 }}>{margin !== null ? `+${margin}%` : '—'}</span>} last />
            </div>
          </ScCard>

          {/* Stock */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader
              title={intl.formatMessage({ id: 'warehouse.stock', defaultMessage: 'Stock' })}
              icon={<IconWarehouse style={{ width: 16, height: 16 }} />}
              actions={<Button kind={ButtonKind.Ghost} icon={IconRefresh}>{intl.formatMessage({ id: 'common.sync', defaultMessage: 'Sync' })}</Button>}
            />
            <div style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--paper)', display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color: status === 'ok' ? 'var(--brand-green)' : status === 'low' ? 'var(--warning)' : 'var(--danger)' }}>
                {onHand}
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'warehouse.unitsLeft', defaultMessage: 'units left' })}</span>
            </div>
            <Row2sm label={intl.formatMessage({ id: 'warehouse.reservedInCarts', defaultMessage: 'Reserved (in carts)' })} value="—" />
            <Row2sm label={intl.formatMessage({ id: 'warehouse.incoming', defaultMessage: 'Incoming' })} value="—" />
            <Row2sm label={intl.formatMessage({ id: 'warehouse.reorderThreshold', defaultMessage: 'Reorder threshold' })} value={intl.formatMessage({ id: 'warehouse.reorderThresholdValue', defaultMessage: '≤ {threshold} units' }, { threshold: 5 })} />
            <Row2sm label={intl.formatMessage({ id: 'warehouse.estDaysToZero', defaultMessage: 'Est. days to zero' })} value="—" last />
          </ScCard>

          {/* Activity timeline */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader title={intl.formatMessage({ id: 'warehouse.activity', defaultMessage: 'Activity' })} subtitle={intl.formatMessage({ id: 'warehouse.activitySubtitle', defaultMessage: 'Most recent first' })} />
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: 'var(--line)' }} />
              {TIMELINE_MOCK.map((ev, i) => {
                const c = TONE_COLOR[ev.tone] || 'var(--muted-2)';
                return (
                  <div key={i} style={{ position: 'relative', paddingLeft: 24, paddingBottom: 14 }}>
                    <span style={{ position: 'absolute', left: 0, top: 2, width: 16, height: 16, borderRadius: 8, background: 'var(--surface)', display: 'grid', placeItems: 'center' }}>
                      <span style={{ width: 10, height: 10, borderRadius: 5, background: c, boxShadow: ev.active ? `0 0 0 3px ${c}33` : 'none' }} />
                    </span>
                    <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: ev.active ? c : 'var(--ink)' }}>{ev.label}</div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>{ev.sub}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)', fontVariantNumeric: 'tabular-nums', marginTop: 3 }}>{ev.date}</div>
                  </div>
                );
              })}
            </div>
          </ScCard>
        </div>
      </div>

      {/* Edit drawer overlay */}
      {editOpen && <EditDrawer product={product} onClose={closeEdit} />}
    </>
  );
};

export default ProductDetails;
